import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { ArrowLeft, Eye, EyeOff, Music, Megaphone, Building2, Video, Palette } from "lucide-react";
import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";
const FRONTEND_URL = typeof window !== "undefined" ? window.location.origin : process.env.REACT_APP_FRONTEND_URL;

const VALID_ROLES = ["promoter", "artist", "label", "video_editor"];

const RegisterPage = () => {
  const [searchParams] = useSearchParams();
  const presetRole = searchParams.get("role");
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    role: VALID_ROLES.includes(presetRole) ? presetRole : "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleGoogleSignup = async () => {
    setGoogleLoading(true);
    try {
      const redirectUri = `${FRONTEND_URL}/auth/google/callback`;
      const response = await axios.get(`${API_URL}/api/auth/google/url`, {
        params: { redirect_uri: redirectUri }
      });
      window.location.href = response.data.auth_url;
    } catch (error) {
      toast.error("Failed to initialize Google signup");
      setGoogleLoading(false);
    }
  };

  const roles = [
    {
      id: "promoter",
      title: "Promoter",
      description: "I promote music on social platforms",
      icon: <Megaphone className="w-6 h-6" />,
    },
    {
      id: "artist",
      title: "Artist",
      description: "I'm a musician looking for promotion",
      icon: <Music className="w-6 h-6" />,
    },
    {
      id: "label",
      title: "Label",
      description: "I manage multiple artists",
      icon: <Building2 className="w-6 h-6" />,
    },
    {
      id: "video_editor",
      title: "Visual Artist",
      description: "Videos & cover art",
      icon: (
        <div className="flex items-center gap-1">
          <Video className="w-5 h-5" />
          <Palette className="w-5 h-5" />
        </div>
      ),
    },
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.role) {
      toast.error("Please select a role");
      return;
    }

    if (!formData.name) {
      toast.error("Please enter your name");
      return;
    }

    if (!formData.password || formData.password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }

    setLoading(true);

    try {
      await register(formData.email, formData.password, formData.name, formData.role);
      toast.success("Account created successfully!");
      navigate("/dashboard");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex">
      {/* Left Side - Image */}
      <div className="hidden lg:block w-1/2 relative">
        <img
          src="https://images.unsplash.com/photo-1766592585929-7bab0c0fb8b3?crop=entropy&cs=srgb&fm=jpg&q=85"
          alt="Concert crowd"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black/60" />
        <div className="absolute bottom-12 left-12 right-12 text-white">
          <p className="font-['Syne'] text-3xl font-bold leading-tight">
            Join the platform that's changing how music gets discovered.
          </p>
        </div>
      </div>

      {/* Right Side - Form */}
      <div className="w-full lg:w-1/2 flex flex-col justify-center px-5 sm:px-8 md:px-16 lg:px-24 py-8 sm:py-12">
        <Link
          to="/"
          className="flex items-center gap-2 text-zinc-500 hover:text-white transition-colors mb-8"
          data-testid="back-link"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to home
        </Link>

        <div className="max-w-md">
          <Link to="/" className="inline-block mb-8" data-testid="logo-link">
            <span className="font-['Syne'] text-2xl font-extrabold tracking-tighter text-white">
              DOPE MEDIA
            </span>
          </Link>

          <h1
            className="font-['Syne'] text-4xl md:text-5xl font-bold tracking-tight mb-4 text-white"
            data-testid="register-title"
          >
            CREATE
            <br />
            ACCOUNT
          </h1>
          <p className="text-zinc-400 mb-8">
            Choose your role and start growing your music today.
          </p>

          <form onSubmit={handleSubmit} className="space-y-6" data-testid="register-form">
            {/* Role Selection */}
            <div className="space-y-3">
              <Label className="form-label text-zinc-400">I am a</Label>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {roles.map((role) => (
                  <button
                    key={role.id}
                    type="button"
                    onClick={() => setFormData({ ...formData, role: role.id })}
                    className={`p-4 border text-center transition-all ${
                      formData.role === role.id
                        ? "border-[#CCFF00] bg-[#CCFF00] text-black"
                        : "border-[#2A2A2A] hover:border-[#CCFF00] text-white"
                    }`}
                    data-testid={`role-${role.id}`}
                  >
                    <div className="mx-auto mb-2">
                      {role.icon}
                    </div>
                    <div className="font-bold text-xs sm:text-sm">{role.title}</div>
                  </button>
                ))}
              </div>
              {formData.role && (
                <p className="text-sm text-zinc-500 mt-2">
                  {roles.find((r) => r.id === formData.role)?.description}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="name" className="form-label text-zinc-400">
                Full Name
              </Label>
              <Input
                id="name"
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="John Doe"
                required
                className="form-input h-12 bg-[#141414] border-[#2A2A2A] text-white placeholder:text-zinc-600 focus:border-[#CCFF00]"
                data-testid="register-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email" className="form-label text-zinc-400">
                Email
              </Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="you@example.com"
                required
                className="form-input h-12 bg-[#141414] border-[#2A2A2A] text-white placeholder:text-zinc-600 focus:border-[#CCFF00]"
                data-testid="register-email-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="form-label text-zinc-400">
                Password
              </Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={formData.password}
                  onChange={(e) =>
                    setFormData({ ...formData, password: e.target.value })
                  }
                  placeholder="••••••••"
                  required
                  minLength={6}
                  className="form-input h-12 pr-12 bg-[#141414] border-[#2A2A2A] text-white placeholder:text-zinc-600 focus:border-[#CCFF00]"
                  data-testid="register-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white"
                  data-testid="toggle-password"
                >
                  {showPassword ? (
                    <EyeOff className="w-5 h-5" />
                  ) : (
                    <Eye className="w-5 h-5" />
                  )}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="btn-primary w-full h-14 flex items-center justify-center"
              data-testid="register-submit-btn"
            >
              {loading ? <div className="spinner" /> : "Create Account"}
            </Button>
          </form>

          {/* Divider */}
          <div className="flex items-center gap-4 my-6">
            <div className="flex-1 h-px bg-[#2A2A2A]"></div>
            <span className="text-sm text-zinc-500">or</span>
            <div className="flex-1 h-px bg-[#2A2A2A]"></div>
          </div>

          {/* Google Signup Button */}
          <Button
            type="button"
            variant="outline"
            className="w-full h-14 flex items-center justify-center gap-3 border-2 border-[#2A2A2A] text-white hover:border-[#CCFF00] bg-transparent"
            onClick={handleGoogleSignup}
            disabled={googleLoading}
            data-testid="google-signup-btn"
          >
            {googleLoading ? (
              <div className="w-5 h-5 border-2 border-zinc-600 border-t-[#CCFF00] rounded-full animate-spin" />
            ) : (
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
            )}
            Sign up with Google
          </Button>

          <p className="text-center mt-8 text-zinc-500">
            Already have an account?{" "}
            <Link
              to="/login"
              className="font-bold text-[#CCFF00] hover:text-[#B8E600] transition-colors"
              data-testid="login-link"
            >
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
